import React from "react";

/**
 * A count chip that doubles as a quick filter. `active` is announced via aria-pressed.
 * Without `onClick` it renders as plain text (a summary, not a control).
 */
interface Props {
  label: React.ReactNode;
  count: number;
  active?: boolean;
  onClick?: () => void;
  tone?: "neutral" | "warn" | "danger" | "ok";
  title?: string;
}

const TONES: Record<NonNullable<Props["tone"]>, string> = {
  neutral: "border-gray-700 bg-gray-800 text-gray-200",
  ok: "border-green-700/50 bg-green-500/10 text-green-200",
  warn: "border-yellow-700/50 bg-yellow-500/10 text-yellow-200",
  danger: "border-red-700/50 bg-red-500/10 text-red-200",
};

const StatChip: React.FC<Props> = ({ label, count, active, onClick, tone = "neutral", title }) => {
  const cls = `inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs ${TONES[tone]} ${
    active ? "ring-2 ring-cyan-500 ring-offset-1 ring-offset-gray-900" : ""
  }`;
  const body = (
    <>
      {label}
      <span className="font-semibold tabular-nums text-white">{count.toLocaleString()}</span>
    </>
  );
  if (!onClick) {
    return (
      <span className={cls} title={title}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={Boolean(active)}
      title={title}
      className={`${cls} hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500`}
    >
      {body}
    </button>
  );
};

export default StatChip;
